import type { JSX, ReactNode, ComponentPropsWithoutRef } from 'react';
import { styled, css } from 'styled-components';
import { visuallyHiddenStyle } from '../../css/visuallyHiddenStyle';

type StyledTableProps = {
  $divided?: boolean;
  $striped?: boolean;
  $visuallyHiddenHeader?: boolean;
};

const StyledTable = styled.table<StyledTableProps>`
  width: 100%;
  border-collapse: collapse;

  ${({ $divided }) =>
    $divided &&
    css`
      tr {
        border-bottom: 1px solid var(--wui-color-border);
      }
    `}

  ${({ $striped }) =>
    $striped &&
    css`
      tbody tr:nth-child(even) {
        background-color: var(--wui-color-bg-surface-secondary);
      }
    `}

  ${({ $visuallyHiddenHeader }) =>
    $visuallyHiddenHeader &&
    css`
      thead {
        ${visuallyHiddenStyle}
      }
    `}
`;

export type TableProps = ComponentPropsWithoutRef<'table'> & {
  /**
   * Should be some combination of `TableHead` and/or `TableBody` and/or `TableFoot`
   */
  children: ReactNode;
  /**
   * If true, will add dividing lines between rows
   */
  divided?: boolean;
  /**
   * If true, will stripe every other row
   */
  striped?: boolean;
  /**
   * If true, will visually hide the table header while keeping it accessible to screen readers
   *
   * @see https://www.accessibility-developer-guide.com/examples/tables/hidden-headers/
   */
  visuallyHiddenHeader?: boolean;
};

/**
 * Semantic table element for presenting data
 */
export const Table = ({
  children,
  divided = false,
  striped = false,
  visuallyHiddenHeader = false,
  ...props
}: TableProps): JSX.Element => {
  return (
    <StyledTable
      $divided={divided}
      $striped={striped}
      $visuallyHiddenHeader={visuallyHiddenHeader}
      {...props}
    >
      {children}
    </StyledTable>
  );
};
